import { memo } from 'react';
import { saudacaoCliente } from '../../domain/usecases/clienteDisplay';
import { ClienteAvatarChip } from './ClienteAvatarChip';

interface Props {
  logoUrl:           string;
  telaNome:          string;
  clienteNome?:      string | null;
  clienteAvatarUrl?: string | null;
  tema:              {
    headerBg:     string;
    headerBorder: string;
    titulo:       string;
    tituloColor:  string;
    badgeBg:      string;
    badgeBorder:  string;
    accentColor:  string;
  };
  minhaPosicaoFila:  number | null;
  etaLabel?:         string | null;
  tvOnline:          boolean;
}

/**
 * Barra do Palco — presença da TV + posição na fila.
 * Âncora: cue “No ar” quando a TV está online.
 */
function JukeboxHeaderImpl({
  logoUrl, telaNome, clienteNome, clienteAvatarUrl, tema, minhaPosicaoFila, etaLabel, tvOnline,
}: Props) {
  return (
    <div className={`sticky top-0 z-20 backdrop-blur-xl border-b px-4 pb-3 pt-[calc(0.75rem+env(safe-area-inset-top))] transition-colors duration-700 ${tema.headerBg} ${tema.headerBorder}`}>
      <div className="flex items-center gap-3">
        <img src={logoUrl} alt={`Logo ${telaNome}`} className="h-8 w-auto object-contain shrink-0" />
        <div className="w-px h-5 shrink-0 bg-white/[0.06]" />
        <div className="min-w-0 flex-1">
          <h1 className={`text-sm font-bold leading-none tracking-tight transition-colors duration-700 ${tema.tituloColor}`}>
            {tema.titulo}
          </h1>
          <p className="mt-0.5 truncate text-xs text-white/50">{telaNome}</p>
          {clienteNome && (
            <div className="mt-1 flex min-w-0 items-center gap-1.5">
              <ClienteAvatarChip
                nome={clienteNome}
                avatarUrl={clienteAvatarUrl}
                size="sm"
                showNome={false}
              />
              <span className="truncate text-xs font-medium text-emerald-400/90">
                {saudacaoCliente(clienteNome)}
              </span>
            </div>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          {minhaPosicaoFila !== null && (
            <div className={`flex items-center gap-1 rounded-full border px-2.5 py-1 transition-colors duration-700 ${tema.badgeBg} ${tema.badgeBorder}`}>
              <svg className={`h-3 w-3 transition-colors duration-700 ${tema.accentColor}`} fill="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path d="M12 3v10.55A4 4 0 1 0 14 17V7h4V3h-6z" />
              </svg>
              <span
                className={`text-xs font-bold tabular-nums transition-colors duration-700 ${tema.accentColor}`}
                aria-label={
                  etaLabel
                    ? `Você está em ${minhaPosicaoFila}º na fila, ${etaLabel}`
                    : `Você está em ${minhaPosicaoFila}º na fila`
                }
              >
                {etaLabel
                  ? `${minhaPosicaoFila}° · ${etaLabel}`
                  : `${minhaPosicaoFila}° na fila`}
              </span>
            </div>
          )}
          <div className="flex items-center gap-1.5 rounded-full bg-white/[0.04] px-2 py-1" aria-live="polite">
            <span
              key={String(tvOnline)}
              className={`h-1.5 w-1.5 animate-scale-in rounded-full ${
                tvOnline ? 'animate-pulse-slow bg-emerald-400' : 'bg-white/25'
              }`}
            />
            <span className={`text-xs font-medium ${tvOnline ? 'text-emerald-400/90' : 'text-white/35'}`}>
              {tvOnline ? 'No ar' : 'offline'}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

export const JukeboxHeader = memo(JukeboxHeaderImpl);
